import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CardFooter } from './card_footer.js';
import { SKIRT_ACTIONS, SKIRT_FOOT, SkirtConfirm } from './dates_skirt.js';
import { WhenSheet, type WhenSheetSlots } from './when_sheet.js';
import { ArrowRightIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { WhenTrigger } from './when_trigger.js';
import './flight_planner.css';

/** Respiro entre o gatilho e o popover flutuante — o MESMO `6px` do
 *  `calc(100% + 6px)` que o `.fpl-pop` de sempre usa em CSS (`flight_planner.css`).
 *  `float` reproduz esse respiro em coordenadas de viewport, então os dois
 *  caminhos (CSS ancorado / portal fixo) pousam no mesmo lugar visual. */
const FLOAT_POP_GAP = 6;

/** Acima de tudo no `document.body` — a régua de todo popover portado (hoje o
 *  do `PlaceField` faz o mesmo via Base UI): um popover portado sai do
 *  stacking context do host (`.bpx-panel`/`.fxo-panel`, z-index local) e precisa
 *  do próprio andar pra não ficar atrás dele. */
const FLOAT_POP_Z = 1100;

/**
 * `right` do popover END-alinhado, clampado pra nunca empurrar a borda ESQUERDA
 * dele pra fora do viewport (CodeRabbit #2174, rodada 3).
 *
 * `right` é a distância da borda direita do popover até a borda direita da
 * viewport; a borda ESQUERDA dele fica em `viewportWidth - right - popWidth`.
 * O alinhamento de sempre (`viewportWidth - triggerRight`) supõe que o
 * gatilho tem espaço à esquerda para o popover inteiro — verdade perto da
 * borda DIREITA da tela, mas não perto da ESQUERDA: o `max-width` do CSS tampa
 * o TAMANHO do popover, nunca a POSIÇÃO, então um gatilho com pouco espaço à
 * esquerda (a 1ª coluna do stepper multi, por exemplo) empurrava a borda
 * esquerda do popover para NEGATIVO — cortado pela própria viewport.
 *
 * `popWidth` ausente (1º commit do `useLayoutEffect`, antes de medir o
 * próprio popover, ou `offsetWidth` indisponível) devolve o alinhamento puro
 * de sempre: não há o que clampar sem saber o tamanho.
 */
export function clampEndRight(
  triggerRight: number,
  popWidth: number | undefined,
  viewportWidth: number,
): number {
  const desired = viewportWidth - triggerRight;
  if (!popWidth) return desired;
  return Math.min(desired, viewportWidth - popWidth);
}

/** Coordenadas de VIEWPORT do popover flutuante — só o necessário pra este
 *  popover (que sempre abre ABAIXO do gatilho, nunca vira acima: o teto de
 *  altura + rolo interno do `.fpl-pop` já cobrem viewport curta). Por isso é
 *  mais simples que a âncora genérica de um combobox: sem
 *  flip vertical, sem clamp de largura — a largura aqui é `max-content`, não a
 *  do campo. `left` XOR `right`: um só lado fica fixo (o outro seria uma
 *  informação redundante que a CSS não usa). */
interface FloatingPopAnchor {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Mede o GATILHO (não o popover — a largura dele é intrínseca) e devolve onde o
 * popover flutuante pousa, em coordenadas de viewport. Mesma receita clássica de
 * âncora medida (medir, portar, reposicionar em scroll/resize) — mas mais
 * simples, pelas razões do `FloatingPopAnchor`.
 *
 * `align`: 'end' alinha a borda DIREITA do popover à borda direita do gatilho —
 * a mesma intenção do `align="end"` que o CSS de sempre expressa via
 * `.fpl-band .fpl-pop`/`.fpl-filter .fpl-pop`/`.fpl-leg-open .fpl-pop` (o QUANDO
 * é a última coluna da fileira, e um popover mais largo saindo pela ESQUERDA
 * dele fica dentro da tela). 'start' alinha pela esquerda — o default de sempre
 * (`left: 0`), usado só quando nenhuma dessas classes-ancestrais aplicaria hoje.
 *
 * **Reancora em `animationend`/`transitionend`** — achado numa verificação
 * visual ao vivo (não capturável em jsdom, que não anima): os DOIS hosts da
 * pill entram com uma animação própria de 0,2s (`bpx-drop`/`fxo-drop`,
 * `booking.css`/`explorer.css` — `opacity` + `translateY(-8px→0)`), e
 * `initialDatesOpen` faz o popover nascer ABERTO no MESMO commit de montagem
 * do painel — o `useLayoutEffect` daqui mede o gatilho antes de o layout do
 * subtree recém-inserido assentar de verdade (o combobox de aeroportos não
 * sofre disso: o foco dele entra por um `useEffect` PASSIVO do overlay, que só
 * roda depois da 1ª pintura, com o painel já visualmente estável). A 1ª medida
 * some SER a certa a maior parte do tempo — os listeners aqui são a rede de
 * segurança de quando não é: os dois eventos de fim de animação/transição
 * BORBULHAM até a window sem precisar de `capture` (ao contrário do `scroll`).
 */
function useFloatingPopAnchor(
  open: boolean,
  align: 'start' | 'end',
  triggerRef: RefObject<HTMLElement | null>,
  popRef: RefObject<HTMLElement | null>,
): FloatingPopAnchor | null {
  const [anchor, setAnchor] = useState<FloatingPopAnchor | null>(null);
  // Closed, the anchor goes (during render): the next opening starts from the floor.
  if (!open && anchor !== null) setAnchor(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next: FloatingPopAnchor =
        align === 'end'
          ? {
              top: rect.bottom + FLOAT_POP_GAP,
              // Clamp pela largura do PRÓPRIO popover — ver `clampEndRight`: sem
              // ele, um gatilho perto da borda ESQUERDA empurrava a borda
              // esquerda do popover pra fora da tela.
              right: clampEndRight(rect.right, popRef.current?.offsetWidth, window.innerWidth),
            }
          : { top: rect.bottom + FLOAT_POP_GAP, left: rect.left };
      setAnchor((prev) =>
        prev && prev.top === next.top && prev.left === next.left && prev.right === next.right
          ? prev
          : next,
      );
    };
    measure();
    const remeasure = (event: Event) => {
      // Rolar o CONTEÚDO do próprio popover não move o gatilho — só o scroll
      // de fora pede re-medida.
      if (event.target instanceof Node && popRef.current?.contains(event.target)) return;
      measure();
    };
    // `capture`: o gatilho vive dentro do ancestral que clipa (`.bpx-panel`/
    // `.fxo-panel`, com `overflow-y: auto`) — o `scroll` dele não borbulha até a
    // window, só a fase de captura o vê.
    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);
    // Sem `capture`: os dois eventos de assentamento borbulham (a animação do
    // painel hospedeiro, ou qualquer transição futura no caminho até o
    // gatilho) — é a MESMA função `remeasure`, que já ignora o que vem de
    // dentro do próprio popover.
    window.addEventListener('animationend', remeasure);
    window.addEventListener('transitionend', remeasure);
    return () => {
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('animationend', remeasure);
      window.removeEventListener('transitionend', remeasure);
    };
  }, [open, align, triggerRef, popRef]);
  return anchor;
}

/**
 * A mecânica comum dos campos colapsados: um gatilho que abre um popover
 * ancorado, e Escape / clique-fora que o fecham chamando `onDismiss` (quem
 * restaura — ou não — o estado do form é o dono do callback: os painéis
 * TRANSACIONAIS da faixa restauram; o popover de datas do layout aberto é
 * não-transacional e passa um noop, ver `WhenFieldView`).
 *
 * `onDismiss` vive num ref porque o pai a recria a cada render: listá-la nas deps
 * re-assinaria os listeners do documento em todo render.
 *
 * `controlled` (par `open`/`onOpenChange`) existe pro layout aberto (#2136): o
 * host pode pedir o popover de datas já aberto (`initialDatesOpen`) e escolher
 * o dia no multi fecha o popover da perna — abertura é estado DO FORM nesses
 * casos, não do campo. Ausente, o campo se governa (band/colapsado).
 *
 * `escCapture`: o popover de DATAS consome o próprio Escape em CAPTURA, com
 * `preventDefault` + `stopImmediatePropagation` — a mesma régua do popover do
 * QUEM (`travelers_field.tsx`): os overlays da pill (#2129/#2135) escutam
 * Escape no `document` em bolha e foram registrados ANTES, então sem a captura
 * o Esc fecharia o overlay inteiro junto com o popover. Seguro AQUI porque a
 * grade de datas não tem consumidor interno de Esc; o painel de TRECHOS (que
 * hospeda comboboxes, donos do próprio Esc no keydown do input) fica em bolha.
 */
function useCollapsedPanel(
  onDismiss: () => void,
  disabled: boolean,
  opts: {
    escCapture?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Camada em FOLHA (Drawer do DS, `WhenSheet`): clique fora e Escape são
     *  do Drawer — este hook não escuta o `document`, senão um toque dentro da
     *  folha portada contaria como "fora" e a derrubaria. */
    passive?: boolean;
  } = {},
) {
  const [openLocal, setOpenLocal] = useState(false);
  const open = opts.open ?? openLocal;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Stable, reading the latest host callbacks: the effects below (which tell the
  // host its panel closed) never re-arm on them.
  const dismissHost = useStableCallback(onDismiss);
  const setOpen = useStableCallback((next: boolean) => {
    setOpenLocal(next);
    opts.onOpenChange?.(next);
  });

  // Congelar com o painel aberto DESCARTA a sessão (mesmo guard do
  // busca de hotel): `readOnly` alterna com o busy da chrome, e sem isto o
  // painel voltaria sozinho quando a busca assentasse, com uma edição pela metade.
  useEffect(() => {
    if (disabled && open) {
      dismissHost();
      setOpen(false);
    }
  }, [disabled, open, dismissHost, setOpen]);

  // Uma TROCA de estado que acha o foco caído no body devolve-o ao gatilho.
  // Fechando: o clique no dia que fecha o popover (#2136) — o botão clicado
  // desmonta junto com a camada. Abrindo: o preenchimento guiado abre esta
  // camada logo depois de o campo anterior desmontar com o foco dentro, e sem
  // isto o Tab seguinte recomeçaria do topo da página em vez de entrar na
  // grade. Só resgata do body, nunca rouba de onde o foco já pousou.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current !== open && document.activeElement === document.body) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  const passive = opts.passive === true;
  useEffect(() => {
    if (!open || passive) return undefined;
    const dismiss = () => {
      dismissHost();
      setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // O popup do campo de praça (PlaceField) sai por PORTAL pro `document.body`
      // — fora do `rootRef`. Escolher um aeroporto lá dentro não é clique
      // fora: sem este guard o painel fechava e o descarte revertia a viagem
      // inteira — o preço de sempre de lista portada.
      if (target instanceof Element && target.closest('[data-slot="popover-content"]')) return;
      if (rootRef.current && !rootRef.current.contains(target)) dismiss();
    };
    const capture = opts.escCapture === true;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (capture) {
        // O Escape que fecha este popover morre aqui — ver a doc do hook.
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      // Fechar por teclado devolve o foco ao gatilho (senão ele cai no body).
      triggerRef.current?.focus();
      dismiss();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, capture);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, capture);
    };
  }, [open, passive, opts.escCapture, dismissHost, setOpen]);

  return { open, rootRef, triggerRef, setOpen };
}

/**
 * O `style` inline do popover FLUTUANTE — pura, fora de `PlannerWhenField` pelo
 * teto de complexidade dele (o cálculo em si é só spread condicional de
 * `left`/`right`, mas somado ao resto do componente estourava o cap).
 *
 * **`left`/`right` sempre os DOIS, nunca só um** — achado numa verificação
 * visual ao vivo (jsdom não resolve cascata CSS de verdade, então nenhum teste
 * pegava isto): `.fpl-pop` no CSS de sempre declara `left: 0` (a âncora do
 * modo NÃO flutuante). Um `style` inline que só seta `right` não CANCELA esse
 * `left: 0` da folha — inline vence stylesheet propriedade a propriedade, e
 * sem um `left` inline a folha continua valendo. Com `left` E `right` os dois
 * resolvidos (não-`auto`) e `width: auto` (o `.fpl-pop` mede pelo conteúdo,
 * `width: max-content`), a CSS 2.1 §10.3.7 é explícita: o par é
 * "over-constrained" e o navegador IGNORA `right`, fica só com `left` — o
 * popover `align="end"` colapsava pra `left: 0`, a borda esquerda da tela,
 * exatamente o defeito reportado. A cura: o lado que NÃO ancora sempre vira
 * `'auto'` explícito no mesmo objeto de `style`, nunca ausente.
 *
 * `anchor: null` (o 1º commit do `open`, antes de o `useLayoutEffect` medir o
 * gatilho) devolve um chão NEUTRO — nunca um popover sem `style` nenhum, que
 * cairia no `position: absolute` do CSS de sempre relativo a um ancestral que,
 * fora da árvore do form (o portal já monta no `document.body`), não é o
 * gatilho. O popover MONTA de qualquer forma nesse 1º commit (ver
 * `PlannerWhenField` — o portal não espera a âncora): o contrato de foco dos
 * overlays da pill (`explorer_planner_overlay.tsx`/`BookingPlannerOverlay.tsx`)
 * faz `panel`/`document`.`querySelector` na PRÓPRIA passive effect da montagem,
 * e esperar a âncora deixaria a célula ausente do DOM bem quando esse efeito
 * lê — o React resolve o `useLayoutEffect` (e o commit que ele dispara) ANTES
 * da pintura, mas não necessariamente antes do passive effect de um ANCESTRAL
 * que já rodou no commit anterior (foi assim que o bug #2174 apareceu:
 * `focusDelivered` gravava `true` cedo demais, focando o painel em vez da
 * célula, e nunca tentava de novo). O chão neutro nunca pinta de verdade —
 * `useLayoutEffect` resolve a âncora real no MESMO commit, antes do browser
 * pintar — o chão respeita `align` (e não sempre `left:0`) porque, se a
 * verificação ao vivo mostrar de novo um 1º frame visível (a régua de
 * `remeasure` do `useFloatingPopAnchor` é a rede de segurança do CASO
 * PERSISTENTE, não deste), o lado errado dói mais que o valor errado.
 */
function floatPopStyle(anchor: FloatingPopAnchor | null, align: 'start' | 'end'): CSSProperties {
  if (!anchor) {
    return align === 'end'
      ? { position: 'fixed', top: 0, left: 'auto', right: 0, zIndex: FLOAT_POP_Z }
      : { position: 'fixed', top: 0, left: 0, right: 'auto', zIndex: FLOAT_POP_Z };
  }
  return {
    position: 'fixed',
    top: anchor.top,
    // Sempre os DOIS — nunca um ausente (ver a doc acima: um `right` sem
    // `left: 'auto'` explícito perde pro `left: 0` da folha).
    left: anchor.left !== undefined ? anchor.left : 'auto',
    right: anchor.right !== undefined ? anchor.right : 'auto',
    zIndex: FLOAT_POP_Z,
  };
}

/**
 * O CORPO do popover (dialog + rodapé) — extraído de `PlannerWhenField` pelo
 * teto de complexidade dele: decidir SE o popover é flutuante já é um bom tanto
 * de ramificação (`useFloatingPopAnchor`, o gatilho, o rootRef), e desenhar o
 * QUE vai dentro (a saia default vs. `footer` custom) é outro tanto — separados,
 * cada função cabe no teto; juntos, não cabiam.
 */
function WhenPopBody({
  popRef,
  float,
  style,
  panelLabel,
  note,
  footer,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  close,
  sheet,
  children,
}: Readonly<{
  readonly popRef: RefObject<HTMLDivElement | null>;
  readonly float: boolean;
  /** Dentro da FOLHA (`WhenSheet`): sem a casca flutuante do `.fpl-pop` — o
   *  miolo é o `TravelCalendar` em `layout="sheet"` (barra de meses pregada,
   *  grade rolando, rodapé pregado, #3446), que já vem em `children` com o
   *  próprio rodapé; `footer`/`confirmLabel`/`note` daqui não valem. O diálogo
   *  é o Drawer. */
  readonly sheet?: WhenSheetSlots;
  /** Presente sempre que `float` (ver `floatPopStyle`): mesmo ANTES de a âncora
   *  medir o gatilho, um chão neutro — o popover MONTA de imediato (nunca
   *  espera a âncora, ver `portalWhenPop`), e o `useLayoutEffect` substitui
   *  este valor pela posição real no mesmo commit, antes da pintura. */
  readonly style?: CSSProperties;
  readonly panelLabel: string;
  readonly note?: string;
  readonly footer?: ((close: () => void) => ReactNode) | null;
  readonly confirmLabel: string;
  readonly confirmDisabled: boolean;
  readonly onConfirm?: () => void;
  readonly close: () => void;
  readonly children: ReactNode;
}>) {
  if (sheet) return children;
  return (
    <div
      ref={float ? popRef : undefined}
      className="fpl-pop"
      role="dialog"
      aria-label={panelLabel}
      {...(style
        ? {
            style,
            // Um mousedown aqui dentro é um mousedown DENTRO do campo — era
            // literalmente isso antes do portal (o popover vivia sob `rootRef`
            // de `PlannerWhenField`). O portal não pode mudar o que "dentro"
            // quer dizer pra quem escuta o `document` (`useCollapsedPanel`,
            // "clique fora" fecha o popover); sem isto, ESCOLHER O DIA fecharia
            // o popover ANTES de o clique do dia sequer resolver — receita
            // clássica de overlay portado.
            onMouseDown: (event: ReactMouseEvent) => {
              event.stopPropagation();
            },
          }
        : {})}
    >
      {children}
      <WhenPopFooter
        {...(note ? { note } : {})}
        footer={footer}
        confirmLabel={confirmLabel}
        confirmDisabled={confirmDisabled}
        {...(onConfirm ? { onConfirm } : {})}
        close={close}
      />
    </div>
  );
}

/** O rodapé do popover/folha — extraído de `WhenPopBody` quando ele ganhou a
 *  segunda casca (a folha), pelo teto de complexidade. A regra é a mesma nas
 *  duas: `null` remove, função desenha a própria saia, ausente é o primário. */
function WhenPopFooter({
  note,
  footer,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  close,
}: Readonly<{
  readonly note?: string;
  readonly footer?: ((close: () => void) => ReactNode) | null;
  readonly confirmLabel: string;
  readonly confirmDisabled: boolean;
  readonly onConfirm?: () => void;
  readonly close: () => void;
}>) {
  if (footer === null) return null;
  if (footer !== undefined) return footer(close);
  return (
    <CardFooter note={note} className={SKIRT_FOOT}>
      <div className={`fui-actions ${SKIRT_ACTIONS}`}>
        <SkirtConfirm
          disabled={confirmDisabled}
          onClick={() => {
            onConfirm?.();
            close();
          }}
        >
          {confirmLabel}
        </SkirtConfirm>
      </div>
    </CardFooter>
  );
}

/**
 * O campo QUANDO quando não há grade embaixo: a pílula deixa de ser espelho e vira
 * GATILHO — clicar abre a grade de meses (a mesma, injetada em `children`) num
 * popover, com a saia de estado/política e o botão primário que fecha a sessão.
 *
 * No modo aberto a pílula não abre nada porque a grade já está na tela logo
 * abaixo (ver a nota em `planner_form.tsx`); aqui não há grade na tela, então o
 * popover é a única porta — e por isso este gatilho tem hover, chevron e
 * `aria-haspopup`, que o espelho não tem.
 *
 * Serve os DOIS degraus sem grade embaixo — a faixa da bancada e o colapsado —, e
 * a diferença entre eles é só o que o primário faz depois de fechar (ver `onConfirm`).
 */
export function PlannerWhenField({
  label,
  value,
  pendingTail,
  empty,
  disabled,
  panelLabel,
  confirmLabel,
  confirmDisabled,
  note,
  hideLabel = false,
  open: openControlled,
  onOpenChange,
  wanted,
  icon,
  footer,
  onOpen,
  onDismiss,
  onConfirm,
  float,
  align,
  sheet,
  backLabel,
  children,
}: {
  readonly label: string;
  /** A data escolhida já formatada, ou o placeholder de "a definir". */
  readonly value: string;
  /** A METADE que falta, no tom de vazio ("– Volta", #2136): ida e volta com só a
   *  ida escolhida mostra a meia-resposta em vez de fingir campo completo —
   *  o `resumoComVoltaPendente` do desenho. Entra no nome acessível junto. */
  readonly pendingTail?: string;
  /** Sem data escolhida: a pílula fica preenchida em vez de meio-opaca (mesma
   *  régua do espelho — é ela que carrega a informação do que falta). */
  readonly empty: boolean;
  readonly disabled: boolean;
  readonly panelLabel: string;
  readonly confirmLabel: string;
  readonly confirmDisabled: boolean;
  /** Nota do host (prosa de política do turno-âncora): mora no rodapé do popover,
   *  onde o desenho a põe — ao lado do CTA que ela qualifica. */
  readonly note?: string;
  /** Cala o rótulo VISÍVEL da coluna (linhas 2+ do multi, #2136) — o nome
   *  acessível fica no `aria-label` do gatilho. */
  readonly hideLabel?: boolean;
  /** Abertura CONTROLADA (par com `onOpenChange`) — o layout aberto do #2136:
   *  o form é dono de quando o popover abre (pedido do host) e fecha (o dia
   *  escolhido). Ausente, o campo se governa (band). Ver `useCollapsedPanel`. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Ver `WhenTrigger.wanted` — o anel de pendente do preenchimento guiado. */
  readonly wanted?: boolean;
  /** Glifo à esquerda do valor (o CalendarBlank do layout aberto, #2136 — a
   *  gramática do `Quando` do desenho). Ausente, só o caret de sempre. */
  readonly icon?: ReactNode;
  /**
   * O rodapé do popover (#2136): `undefined` mantém o primário de sempre
   * (band/colapsado); `null` REMOVE o rodapé — somente ida fecha no clique do
   * dia e a saia sumiria vazia; uma função recebe o `close` interno (que devolve
   * o foco ao gatilho) e desenha a saia própria — o Redefinir/Confirmar do
   * intervalo do layout aberto.
   */
  readonly footer?: ((close: () => void) => ReactNode) | null;
  readonly onOpen: () => void;
  readonly onDismiss: () => void;
  /** O que o primário DEFAULT faz ALÉM de fechar guardando o que foi escolhido.
   *  Ausente na faixa, e não por economia: lá o CTA da fileira é que busca, então
   *  "Confirmar datas" só fecha a grade. No colapsado este popover é a única
   *  saída do form, e aí ele ECOA a escolha. Ignorado com `footer` custom. */
  readonly onConfirm?: () => void;
  /**
   * Sai por PORTAL pro `document.body` e vira `position: fixed` (#2174): o
   * `.fpl-pop` de sempre é `position: absolute` dentro do form, e dentro dos
   * overlays da pill (`.bpx-panel`/`.fxo-panel`, shell/kit) esse ancestral tem
   * `max-height` + `overflow-y: auto` — o popover nascia CORTADO no pé do
   * painel em vez de flutuar por cima dele. A técnica: medir o GATILHO, portar,
   * reposicionar em scroll/resize.
   *
   * `false` preserva TODO host de hoje (o card do chat, a faixa da bancada, o
   * colapsado) — só os overlays da pill (que são os únicos com o ancestral que
   * clipa) passam `true`. Ver `PlannerFormProps.floatDatesPopover`.
   *
   * Sem default: os TRÊS call sites (`WhenFieldView`/`OpenWhenField`/
   * `LegWhenField`, `planner_form.tsx`) já resolvem o valor e passam sempre —
   * um default aqui só somaria complexidade sem servir consumidor nenhum.
   */
  readonly float: boolean;
  /** Lado que a borda do popover FLUTUANTE acompanha (ignorado sem `float` — a
   *  régua de sempre é puramente CSS, `flight_planner.css`). Ver
   *  `useFloatingPopAnchor`. Sem default pela mesma razão de `float`. */
  readonly align: 'start' | 'end';
  /** A camada é a FOLHA de tela cheia (`WhenSheet`, #2653) em vez do popover
   *  — o palco estreito do layout aberto — e estes são os slots dela (título
   *  por modo, barra de meses, rodapé; #3446). Ignora `float`/`align`: a folha
   *  não ancora em nada. Ausente ⇒ popover, o que preserva todo host. */
  readonly sheet?: WhenSheetSlots;
  /** Nome acessível da seta de voltar da folha; sem ele, o próprio `label`. */
  readonly backLabel?: string;
  readonly children: ReactNode;
}) {
  const { open, rootRef, triggerRef, setOpen } = useCollapsedPanel(onDismiss, disabled, {
    // Datas consomem o próprio Esc em captura — não há consumidor interno de
    // Esc na grade, e é o que preserva o overlay da pill atrás (ver o hook).
    escCapture: true,
    passive: sheet !== undefined,
    ...(onOpenChange ? { open: openControlled ?? false, onOpenChange } : {}),
  });
  const close = () => {
    triggerRef.current?.focus();
    setOpen(false);
  };
  const popRef = useRef<HTMLDivElement>(null);
  const floatAnchor = useFloatingPopAnchor(float && open, align, triggerRef, popRef);
  const popContent = open && !disabled && (
    <WhenPopBody
      popRef={popRef}
      float={float}
      {...(float ? { style: floatPopStyle(floatAnchor, align) } : {})}
      panelLabel={panelLabel}
      {...(note ? { note } : {})}
      footer={footer}
      confirmLabel={confirmLabel}
      confirmDisabled={confirmDisabled}
      {...(onConfirm ? { onConfirm } : {})}
      close={close}
      {...(sheet ? { sheet } : {})}
    >
      {children}
    </WhenPopBody>
  );
  const layer = (
    <WhenLayer
      sheet={sheet}
      float={float}
      open={open && !disabled}
      label={label}
      backLabel={backLabel}
      triggerRef={triggerRef}
      onClose={() => {
        onDismiss();
        close();
      }}
      pop={popContent}
    />
  );
  return (
    <div className="genui2 fpl-field fpl-field-when fpl-collapsed bg-transparent" ref={rootRef}>
      {!hideLabel && <span className="fpl-flabel">{label}</span>}
      <WhenTrigger
        triggerRef={triggerRef}
        label={label}
        value={value}
        {...(pendingTail ? { pendingTail } : {})}
        empty={empty}
        disabled={disabled}
        open={open}
        wanted={wanted}
        {...(icon ? { icon } : {})}
        onToggle={() => {
          (open ? onDismiss : onOpen)();
          setOpen(!open);
        }}
      />
      {layer}
    </div>
  );
}

/** A camada do QUANDO: folha de tela cheia (`WhenSheet`), popover flutuante
 *  (portal) ou popover ancorado — fora de `PlannerWhenField` pelo teto de
 *  complexidade dele, que a folha estourava. */
function WhenLayer({
  sheet,
  float,
  open,
  label,
  backLabel,
  triggerRef,
  onClose,
  pop,
}: Readonly<{
  readonly sheet: WhenSheetSlots | undefined;
  readonly float: boolean;
  readonly open: boolean;
  readonly label: string;
  readonly backLabel: string | undefined;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly pop: ReactNode;
}>): ReactNode {
  if (sheet) {
    return (
      <WhenSheet
        open={open}
        slots={sheet}
        backLabel={backLabel ?? label}
        onClose={onClose}
        returnFocusTo={triggerRef}
      >
        {pop}
      </WhenSheet>
    );
  }
  return float ? portalWhenPop(pop) : pop;
}

/**
 * Portal — monta SEMPRE que `PlannerWhenField` chama (já filtrou `open &&
 * !disabled` em `popContent`), sem esperar a âncora (ver `floatPopStyle`): o
 * contrato de foco dos overlays da pill precisa da célula NO DOM assim que o
 * popover abre, e um gate em `anchor` (a régua clássica de lista ancorada,
 * que não tem esse consumidor) deixava a célula ausente
 * bem quando o `useEffect` do overlay a busca — era o bug #2174 (2ª metade):
 * `focusDelivered` gravava cedo demais, focando o painel em vez da célula.
 * Pura e fora de `PlannerWhenField` pelo teto de complexidade dele.
 */
function portalWhenPop(pop: ReactNode): ReturnType<typeof createPortal> {
  // O portal monta fora da raiz `.genui2` do host — as custom properties do DS
  // só herdam por ancestral (a mesma razão do `genui2` no popover do PlaceField).
  return createPortal(<div className="genui2">{pop}</div>, document.body);
}

/**
 * O multi-trecho colapsado: a viagem inteira numa LINHA — a cadeia de praças e o
 * intervalo de datas — com "Editar trechos" abrindo a lista (e o calendário da
 * perna aberta) num popover.
 *
 * A cadeia é a origem de cada trecho mais o destino do último, então ela prova a
 * viagem toda sem repetir praça: `GRU → REC → SSA → GRU`. É o resumo que devolve
 * a altura que a lista por perna ocupa numa bancada onde ela é só um dos campos.
 *
 * O par Cancelar/Atualizar é TRANSACIONAL: o construtor edita o estado vivo do
 * form (é ele quem encadeia origens, limpa datas fora de ordem e pede o heat da
 * perna), então quem sai por Cancelar tem de voltar ao retrato de quando abriu —
 * o pai guarda esse retrato e o restaura em `onDismiss`.
 */
export function PlannerLegsField({
  chain,
  dates,
  editLabel,
  panelLabel,
  cancelLabel,
  updateLabel,
  updateDisabled,
  disabled,
  note,
  cta,
  onOpen,
  onDismiss,
  onUpdate,
  children,
}: {
  /** Praças em ordem; pontas ainda não escolhidas já vêm com o placeholder. */
  readonly chain: string[];
  /** "Sex, 7 ago – Qui, 13 ago"; ausente enquanto nenhuma data foi escolhida. */
  readonly dates?: string;
  readonly editLabel: string;
  readonly panelLabel: string;
  readonly cancelLabel: string;
  readonly updateLabel: string;
  readonly updateDisabled: boolean;
  readonly disabled: boolean;
  readonly note?: string;
  /**
   * **A saída da faixa, injetada NA LINHA** (mesmo papel do `cta` do
   * `MultiLegBuilder`, mesmo botão — `multiCta` do `planner_form.tsx`). Ausente no
   * COLAPSADO, onde `Atualizar` segue sendo o próprio submit.
   *
   * **Por que ele PRECISA nascer aqui dentro, e não como irmão deste componente na
   * linha do host:** o `useCollapsedPanel` fecha o popover em QUALQUER `mousedown`
   * no `document` fora de `rootRef` (linha abaixo), chamando `onDismiss` —
   * `restoreLegs`, no form — ANTES de o `click` do CTA sequer resolver. Um CTA
   * IRMÃO deste componente conta como "fora": mousedown reverte os trechos
   * editados pro retrato de quando o popover abriu, E SÓ DEPOIS o click de
   * `onUpdate`/submit dispara — busca com o roteiro VELHO, silenciosamente, e só
   * no mouse (teclado não passa por `mousedown`, então divergia por modalidade).
   * Renderizar `cta` aqui dentro é o mesmo material de `rootRef`: nenhum
   * mousedown nele é "fora", nada reverte.
   *
   * A alternativa — `onMouseDown={(e) => e.stopPropagation()}` no wrapper do
   * CTA — resolve pra quem não
   * pode entrar no root (a lista de praças é portal pro `body`). Aqui não há
   * essa amarra: o CTA é nosso JSX, então entrar de verdade é mais robusto que
   * lembrar de blindar toda nova via de fechamento.
   *
   * **Segunda race, achada em Storybook (não no mousedown — no CLIQUE de
   * verdade):** clicar o CTA com o painel `Editar trechos` ainda ABERTO dispara o
   * submit (`multiCta`/`submitMulti`), que marca o explorer `busy` — e `busy` vira
   * `disabled` NESTA prop um render depois. Se o painel seguisse aberto até lá, o
   * efeito `disabled && open` (abaixo) o fecharia chamando `onDismiss`
   * (`restoreLegs`) — revertendo os trechos que ACABARAM de sair na busca, e o
   * `useEffect` de rascunho do form (`planner_form.tsx`) leria essa reversão como
   * DIVERGÊNCIA da baseline que o próprio submit rebaseizou, re-sujando o
   * `onDirtyChange` que já tinha saído `false`. Por isso o wrapper do `cta`
   * chama `close()` no `onClick` (evento de bolha, MESMO tick síncrono do clique):
   * quando o render seguinte marcar `disabled`, `open` já está `false`, e o efeito
   * de congelamento não tem o que fechar. Só dispara quando o clique REALMENTE
   * resolve — um botão `disabled` nunca emite `click`, então nada fecha por engano
   * enquanto o roteiro está incompleto.
   */
  readonly cta?: ReactNode;
  readonly onOpen: () => void;
  readonly onDismiss: () => void;
  readonly onUpdate: () => void;
  readonly children: ReactNode;
}) {
  const { open, rootRef, triggerRef, setOpen } = useCollapsedPanel(onDismiss, disabled);
  const close = () => {
    triggerRef.current?.focus();
    setOpen(false);
  };
  return (
    <div className="fpl-collapsed fpl-chain-wrap" ref={rootRef}>
      <div className="fpl-chain">
        <span className="fpl-chain-route">
          {chain.map((place, i) => (
            <Fragment key={`${place}-${String(i)}`}>
              {i > 0 && <ArrowRightIcon size={12} aria-hidden />}
              <b>{place}</b>
            </Fragment>
          ))}
        </span>
        {dates && <span className="fpl-chain-dates">{dates}</span>}
        <button
          ref={triggerRef}
          type="button"
          className="fpl-chain-edit"
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            if (open) {
              onDismiss();
            } else {
              onOpen();
            }
            setOpen(!open);
          }}
        >
          <PencilSimpleIcon size={14} aria-hidden />
          {editLabel}
        </button>
        {/* `onClick={close}` no wrapper (bolha do clique do botão real, mesmo
            tick síncrono) — ver a doc da prop `cta` sobre a race de congelamento
            que isto evita. Só existe quando há CTA: um wrapper vazio não some do
            layout sozinho. */}
        {cta && (
          // Wrapper que só escuta a BOLHA do clique do botão real dentro de `cta`
          // (ver a doc da prop): não é alvo próprio, então não tem ação a expor ao
          // teclado — quem age é o botão, que já é acessível.
          <span className="fpl-chain-cta" onClick={close} role="presentation">
            {cta}
          </span>
        )}
      </div>
      {open && !disabled && (
        <div className="fpl-pop fpl-pop-legs" role="dialog" aria-label={panelLabel}>
          {children}
          <CardFooter note={note}>
            <div className="fui-actions">
              <button
                type="button"
                className="fui-btn fpl-reset"
                onClick={() => {
                  onDismiss();
                  close();
                }}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className="fui-btn fui-btn-primary fpl-cta"
                disabled={updateDisabled}
                onClick={() => {
                  onUpdate();
                  close();
                }}
              >
                {updateLabel}
              </button>
            </div>
          </CardFooter>
        </div>
      )}
    </div>
  );
}
